import { useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Box, Button, Chip, Collapse, Stack, Tooltip, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import dayjs from 'dayjs';

// Small read-outs of thread.analysis_payload keys written by the backend:
// autosend, needs_investigation, staff_instructions, external_draft.

const asObject = (value) => (value && typeof value === 'object' && !Array.isArray(value) ? value : null);
const formatWhen = (iso) => (iso && dayjs(iso).isValid() ? dayjs(iso).format('ddd MMM D, h:mm A') : '');

/**
 * "Auto-sending at <time>" with Cancel, while an auto-send is queued.
 * `pendingIds`, when given, is the thread's pending scheduled-reply ids: the
 * banner only shows if the auto-send's reply is still among them (the
 * payload state is not rewritten when staff cancel the reply).
 */
export function AutosendBanner({ payload, pendingIds, cancelling, onCancel }) {
  const autosend = asObject(payload?.autosend);
  if (!autosend || autosend.state !== 'scheduled' || !autosend.scheduled_reply_id) return null;
  if (Array.isArray(pendingIds) && !pendingIds.includes(autosend.scheduled_reply_id)) return null;
  const when = formatWhen(autosend.scheduled_at || autosend.would_send_at);
  return (
    <Alert
      severity="warning"
      variant="filled"
      action={
        <Button
          color="inherit"
          size="small"
          disabled={cancelling}
          onClick={() => onCancel(autosend.scheduled_reply_id)}
        >
          {cancelling ? 'Cancelling…' : 'Cancel'}
        </Button>
      }
    >
      Auto-sending{when ? ` at ${when}` : ''}
    </Alert>
  );
}

AutosendBanner.propTypes = {
  payload: PropTypes.object,
  pendingIds: PropTypes.array,
  cancelling: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
};
AutosendBanner.defaultProps = { payload: null, pendingIds: undefined, cancelling: false };

/** Slim "This one may need digging" banner with an Investigate button. */
export function InvestigationBanner({ payload, disabled, onInvestigate }) {
  if (!payload?.needs_investigation) return null;
  const reason = String(payload.investigation_reason || '').trim();
  return (
    <Alert
      severity="info"
      sx={{ py: 0, alignItems: 'center' }}
      action={
        <Button color="inherit" size="small" disabled={disabled} onClick={onInvestigate}>
          Investigate
        </Button>
      }
    >
      <Typography variant="body2">This one may need digging{reason ? `: ${reason}` : ''}</Typography>
    </Alert>
  );
}

InvestigationBanner.propTypes = {
  payload: PropTypes.object,
  disabled: PropTypes.bool,
  onInvestigate: PropTypes.func.isRequired,
};
InvestigationBanner.defaultProps = { payload: null, disabled: false };

/** Chips beside the composer: report-mode auto-send verdict and Claude Code drafts. */
export function ComposerStatusChips({ payload }) {
  const autosend = asObject(payload?.autosend);
  const external = asObject(payload?.external_draft);
  const chips = [];

  if (autosend && autosend.mode === 'report') {
    if (autosend.eligible) {
      const category = autosend.category ? String(autosend.category).replace(/_/g, ' ') : 'unknown';
      chips.push(
        <Tooltip key="autosend" title={`Category: ${category}${autosend.category_enabled ? '' : ' (not enabled)'}`}>
          <Chip label="Would auto-send" size="small" variant="outlined" sx={{ color: 'text.secondary', height: 22 }} />
        </Tooltip>,
      );
    } else if (Array.isArray(autosend.reasons) && autosend.reasons.length) {
      chips.push(
        <Tooltip
          key="autosend"
          title={
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {autosend.reasons.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </Box>
          }
        >
          <Chip
            label="Won't auto-send"
            size="small"
            variant="outlined"
            sx={{ color: 'text.disabled', borderColor: 'divider', height: 22 }}
          />
        </Tooltip>,
      );
    }
  }

  if (external && external.source === 'claude_code') {
    const when = formatWhen(external.at);
    const tip = [external.note, [when, external.by ? `by ${external.by}` : ''].filter(Boolean).join(' ')]
      .filter(Boolean)
      .join(' · ');
    chips.push(
      <Tooltip key="external" title={tip || 'Pushed from a Claude Code chat'}>
        <Chip label="Draft from Claude Code chat" size="small" variant="outlined" color="secondary" sx={{ height: 22 }} />
      </Tooltip>,
    );
  }

  if (!chips.length) return null;
  return (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
      {chips}
    </Stack>
  );
}

ComposerStatusChips.propTypes = { payload: PropTypes.object };
ComposerStatusChips.defaultProps = { payload: null };

/** Collapsed "Earlier notes (N)" list of past regenerate instructions. */
export function StaffInstructionHistory({ payload }) {
  const [open, setOpen] = useState(false);
  const notes = (Array.isArray(payload?.staff_instructions) ? payload.staff_instructions : []).filter(
    (n) => n && typeof n === 'object' && n.text,
  );
  if (!notes.length) return null;
  return (
    <Box>
      <Button
        size="small"
        variant="text"
        color="inherit"
        onClick={() => setOpen((v) => !v)}
        endIcon={
          <ExpandMoreIcon
            sx={{ fontSize: 16, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
          />
        }
        sx={{ textTransform: 'none', fontSize: 12, px: 0.5, minWidth: 0, color: 'text.secondary' }}
      >
        Earlier notes ({notes.length})
      </Button>
      <Collapse in={open} unmountOnExit>
        <Stack spacing={0.75} mt={0.5} pl={0.5}>
          {[...notes].reverse().map((note, idx) => (
            // eslint-disable-next-line react/no-array-index-key
            <Box key={`${note.at || ''}-${idx}`}>
              <Typography variant="body2" sx={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
                {note.text}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {[note.by || 'Unknown', formatWhen(note.at), note.pending ? 'queued' : '']
                  .filter(Boolean)
                  .join(' · ')}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
}

StaffInstructionHistory.propTypes = { payload: PropTypes.object };
StaffInstructionHistory.defaultProps = { payload: null };
